/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  CSSProperties,
  forwardRef,
  memo,
  MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef
} from 'react';
import {areEqual} from 'react-window';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {GlobalGroupProps} from '@ion/core/src/types/general';
import {FloatingIconButtonGroup} from '@ion/dls/src/components/buttons/FloatingIconButton';
import Tooltip from '@ion/dls/src/components/tooltip/Tooltip';
import useMedia from '@ion/dls/src/hooks/useMedia';
import {CreateGeoFenceIcon} from '@ion/dls/src/icons/CreateGeoFenceIcon';
import {MaterialBackIcon} from '@ion/dls/src/icons/MaterialBackIcon';
import CreateGeofences from '@ion/dls/src/maps/geofences/CreateGeofences';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {Color, IconSize} from '@ion/dls/src/types/types';
import {HeaderTypography} from '@ion/dls/src/typography/HeaderTypography';
import {addComponent, removeComponent} from '../helpers/simpleMapUtils';
import {useMapTopOffset} from '../hooks/useMapTopOffset';
import {useMapViewHeight} from '../hooks/useMapViewHeight';
import {useShowTool} from '../hooks/useShowTool';
import {MapToolPositions, MapTools} from '../types/types';

// net adjustment, in pixels, applied against the map's top edge when
// positioning the ported Create/Edit Geofence panel via `usePortal`, tuned
// against the live-verified panel position (widget's #_portal computed
// `top` confirmed correct at 176px)...
const CREATE_GEOFENCE_PANEL_TOP_ADJUSTMENT = -63;

// the ported Create/Edit Geofence panel should span the map's available
// height minus a 24px margin at both the top and bottom...
const CREATE_GEOFENCE_PANEL_HEIGHT_MARGIN = 48;

// defines the style prop definitions...
export interface CreateGeofenceToolProps{
  isSelected?: boolean;
  onClick: MouseEventHandler;
  size: IconSize;
};

// defines the prop definition for CreateGeofenceToolFactory component...
export interface CreateGeofenceToolFactoryProps extends GlobalGroupProps {
  view: __esri.MapView;
  position: MapToolPositions;
  assetCategories?: string[];
  map: __esri.Map,
  handleViewAllClick: Function;
  geofenceSave: Function;
  savingGeofence?: boolean;
  toggleOpenClose?: Function;
  open?: boolean;
  useHereApi?: boolean;
  activeTool: MapTools;
  setActiveTool: Function;
  createPopupStyle?: CSSProperties;
  isAdminGeofence?: boolean;
  isShowViewAllOption?: boolean;
  editGeofenceGraphic?: __esri.Graphic;
  setEditGeofenceGraphic?: Function;
  isOpenFromBottomSheet?: boolean;
  setIsGeofenceContentsOpen?: Function;
  onCloseBottomSheet?: Function;
  currentAccountId?: number;
  reinitialize?: boolean;
  setReinitialize?: Function;
  setSelectedTool?: Function;
  showTriggerIcon?: boolean;
  usePortal?: boolean;
};

// defining style for CreateGeofenceTool components...
const useStyles = makeStyles(theme => ({
  searchView : {
    padding  : '12px',
    minWidth : '348px',
    top      : 0
  },
  breadCrumbWrapper : {
    display        : 'flex',
    justifyContent : 'flex-start',
    alignItems     : 'center',
    background     : dlsColor('grey10'),
    marginTop      : 0,
    '& > span'     : {
      fontWeight  : 500,
      color       : dlsColor('white'),
      marginRight : '5px',
      ...dlsTypography('chipButton')
    },
    '& > span:last-child' : {
      color : dlsColor('white')
    },
    '& > span:first-child' : {
      lineHeight  : 0,
      marginRight : 0
    }
  },
  breadcrumbSeparator : {
    color : dlsColor('white')
  }
}));

/**
   * The CreateGeofenceTool provides the UI for creating boundary/geofence in map view and
   * can be placed on the map layers.
   *
   * ```typescript
   *
   *   <CreateGeoFenceTool size={'medium'} onClick={function() {}} />
   *
   * ```
   */
export const CreateGeoFenceTool = ({size, onClick, isSelected = false}: CreateGeofenceToolProps) => {
  return (
    <FloatingIconButtonGroup>
      <FloatingIconButtonGroup.FloatingIconButton onClick={onClick}
        isSelected={isSelected}>
        <CreateGeoFenceIcon size={size} />
      </FloatingIconButtonGroup.FloatingIconButton>
    </FloatingIconButtonGroup>
  );
};

/**
   * The CreateGeofenceToolFactory component is responsible for creating Boundary/Geofence
   * Tool/widget component that provide an option to draw and choose the geolocation based on
   * user's input. This is a stand-alone tool, can usable in any of position on the map.
   *
   * ```typescript
   *
   *  import CreateGeofenceToolFactory from '../tools/CreateGeofenceTool';
   *
   *  <CreateGeofenceToolFactory view={view} />
   *
   * ```
   */
export const CreateGeofenceToolFactory = ({
  assetCategories,
  map,
  handleViewAllClick,
  geofenceSave,
  savingGeofence,
  view,
  position,
  toggleOpenClose,
  activeTool,
  accountMap,
  globalGroup,
  setActiveTool,
  reinitialize,
  setReinitialize,
  createPopupStyle,
  currentAccountId,
  isShowViewAllOption,
  editGeofenceGraphic,
  setEditGeofenceGraphic,
  setIsGeofenceContentsOpen,
  setSelectedTool,
  onCloseBottomSheet,
  useHereApi = false,
  open = false,
  isAdminGeofence = false,
  isOpenFromBottomSheet = false,
  showTriggerIcon = true,
  usePortal = false
}: CreateGeofenceToolFactoryProps) => {
  const classes                   = useStyles();
  const mapTopOffset              = useMapTopOffset(view);
  const mapViewHeight             = useMapViewHeight(view);
  const geofenceTopOffset         = mapTopOffset !== null
    ? Math.max(mapTopOffset + CREATE_GEOFENCE_PANEL_TOP_ADJUSTMENT, 0)
    : null;
  // `createPopupStyle.height` (built from this) is applied to the panel's
  // wrapper div unconditionally, including on the mobile bottom-sheet path
  // (`isOpenFromBottomSheet`), so this desktop-only, map-height-derived
  // value must not leak into that path...
  const panelHeight               = usePortal && !isOpenFromBottomSheet && mapViewHeight !== null
    ? Math.max(mapViewHeight - CREATE_GEOFENCE_PANEL_HEIGHT_MARGIN, 0)
    : null;
  const mergedCreatePopupStyle    = useMemo(() => (
    panelHeight !== null ? {...createPopupStyle, height: `${panelHeight}px`} : createPopupStyle
  ), [createPopupStyle, panelHeight]);
  const childRef                  = useRef(null);
  const [show, toggleShow, handleClickOutside, setShow]        = useShowTool({
    ref          : childRef,
    initialState : open,
    toolId       : MapTools.createGeoFence,
    activeTool,
    setActiveTool
  });
  const geoFencesGraphicsLayerRef = useRef(null);
  const sketchViewModelRef        = useRef(null);
  const selectedMapPointRef       = useRef(null);
  const geofenceRef               = useRef(null);
  const {isPhone}                 = useMedia();

  const handleOnclick = useCallback((e) => {
    toggleShow(e);
    setSelectedTool(null);
  }, [setSelectedTool]);

  const MapControlsBreadCrumbs = memo(({crumb, isPlainCrumb = false}: any) => {
    return (
      <div className={classes.breadCrumbWrapper}>
        <span onClick={handleOnclick}>
          <MaterialBackIcon color={Color.white} {...!isPlainCrumb && {
            color : Color.white
          }} />
        </span>
        {!isPlainCrumb ? (
          <>
            <span onClick={handleOnclick}>
              {translate('maps:controls.header')}
            </span>
            <span className={classes.breadcrumbSeparator}>
                -
            </span>
            <span>{crumb}</span>
          </>
        ) : (
          <HeaderTypography variant='h4'>
            {crumb}
          </HeaderTypography>
        )}
      </div>
    );
  }, areEqual);

  useEffect(() => {
    geofenceRef.current?.handleBack?.();
  }, [currentAccountId]);

  useEffect(() => {
    if (reinitialize) {
      geofenceRef.current?.handleBack?.();
      setReinitialize(false);
    }
  }, [reinitialize]);

  useEffect(() => {
    if (editGeofenceGraphic) {
      toggleShow();
    }
  }, [editGeofenceGraphic]);

  useEffect(() => {
    setShow(open);
  }, [open]);

  const closeGeoFencePanel = useCallback(() => {
    setEditGeofenceGraphic(null);
    setShow(false);
    setIsGeofenceContentsOpen?.(false);
    onCloseBottomSheet?.();
  }, [setEditGeofenceGraphic, toggleShow, onCloseBottomSheet]);

  // clicking away only ever toggled this component's own local `show`
  // state, but the mount effect below keeps the panel mounted whenever
  // `show || open` is true - `open` is driven by `isCreatingGeofence`
  // upstream (AdminGeofencesMapView.tsx), which a local-only toggle never
  // reset, so the panel never actually unmounted. `closeGeoFencePanel`
  // resets that upstream state too (via `setEditGeofenceGraphic(null)`),
  // matching what the panel's own close (X) button already does...
  const onClose = useCallback(() => {
    if (!editGeofenceGraphic) {
      closeGeoFencePanel();
    }
  }, [editGeofenceGraphic, closeGeoFencePanel]);

  const CreateGeofencesComp = useMemo(() => (
    <div ref={childRef}>
      <CreateGeofences ref={geofenceRef}
        useHereApi={useHereApi}
        accountMap={accountMap}
        globalGroup={globalGroup}
        assetCategories={assetCategories}
        closeGeoFencePanel={closeGeoFencePanel}
        isPhone={isPhone}
        createPopupStyle={mergedCreatePopupStyle}
        isAdminGeofence={isAdminGeofence}
        handleViewAllClick={handleViewAllClick}
        geoFencesGraphicsLayerRef={geoFencesGraphicsLayerRef}
        geofenceSave={geofenceSave}
        onClose={onClose}
        map={map}
        mapView={view}
        MapControlsBreadCrumbs={isOpenFromBottomSheet && MapControlsBreadCrumbs}
        isShowViewAllOption={isShowViewAllOption}
        editGeofenceGraphic={editGeofenceGraphic}
        savingGeofence={savingGeofence}
        sketchViewModelRef={sketchViewModelRef}
        selectedMapPointRef={selectedMapPointRef}
        setIsGeofenceContentsOpen={setIsGeofenceContentsOpen}
        usePortal={usePortal}
        geofenceTopOffset={geofenceTopOffset ?? undefined}
        show={show} />
    </div>
  ), [
    useHereApi,
    assetCategories,
    closeGeoFencePanel,
    isPhone,
    mergedCreatePopupStyle,
    isAdminGeofence,
    handleViewAllClick,
    geofenceSave,
    onClose,
    map,
    view,
    isShowViewAllOption,
    editGeofenceGraphic,
    savingGeofence,
    currentAccountId,
    setIsGeofenceContentsOpen,
    isOpenFromBottomSheet,
    usePortal,
    geofenceTopOffset,
    show
  ]);

  useEffect(() => {
    if (!isOpenFromBottomSheet) {
      if (show || open) {
        addComponent({
          id        : MapTools.mapCreateGeofence,
          component : CreateGeofencesComp,
          position,
          view
        });
      } else {
        removeComponent(MapTools.mapCreateGeofence, view);
      }
    }
  }, [show, open, view, isOpenFromBottomSheet]);

  return isOpenFromBottomSheet ? CreateGeofencesComp : !showTriggerIcon ? null : (
    <Tooltip title={translate('maps:controls.createGeoFence')}>
      <div>
        <CreateGeoFenceTool size={IconSize.medium}
          onClick={toggleShow}
          isSelected={show || open} />
      </div>
    </Tooltip>
  );
};

export default memo(forwardRef(CreateGeofenceToolFactory), areEqual);
